// OP-4: the single client-facing system-status line. It shows ONE honest aggregate derived from a
// real signal (data-freshness) — never a hardcoded "operational". Client-safe: no source names, no
// counts, no cost/queue internals (those live only on the admin Operational page). When the signal
// can't be read it says "status unavailable" rather than a false green.

import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiConfig';

// One status dictionary for the whole platform: reachable + fresh feeds → operational; reachable +
// a stale feed → degraded; reachable but feed telemetry not populated yet (cold start) → initializing
// (NOT "no live signal", which read as "down" while the page was plainly working); unreachable API →
// unavailable. "operational" is still EARNED by real freshness data — never a hollow green.
type Status = 'operational' | 'degraded' | 'reachable' | 'initializing' | 'unavailable';

interface FreshnessSource { stale?: boolean }

export default function SystemStatusFooter({ alertCount = 0 }: { alertCount?: number }) {
  const [status, setStatus] = useState<Status>('initializing');

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(getApiUrl('/api/v2/data-freshness'))
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          if (!alive) return;
          if (!json || json.success !== true) { setStatus('unavailable'); return; }
          const sources: FreshnessSource[] = Array.isArray(json.sources) ? json.sources : [];
          // ARIN SEI small#3: "All systems operational" is a GREEN claim — it must be EARNED by real
          // freshness telemetry, not by reachability alone. With per-source rows: a stale source →
          // "degraded", all fresh → "operational". With NO per-source rows the endpoint cannot confirm
          // every feed is fresh, so we say a neutral "Live" (reachable) rather than a green that
          // contradicts a known off-feed banner elsewhere on the page.
          if (sources.length === 0) { setStatus('reachable'); return; }
          setStatus(sources.some((s) => s.stale) ? 'degraded' : 'operational');
        })
        .catch(() => { if (alive) setStatus('unavailable'); });
    };
    load();
    const t = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // Pre-revenue audit P1: this line is a PLATFORM-WIDE freshness aggregate, distinct from the
  // per-asset "DATA TIER" chip in the header. The old label "Some data sources delayed" sat next to
  // a green "LIVE_VERIFIED" asset tier and read as a contradiction. Scoping it to "platform feeds"
  // (with a tooltip pointing at the per-asset tier) resolves that without naming sources — source
  // names stay on the admin Operational page by design.
  const meta: Record<Status, { dot: string; label: string; text: string; help: string }> = {
    operational: { dot: 'bg-cmd-safe', label: 'All systems operational', text: 'text-cmd-textDim', help: 'Platform feeds reachable and fresh. The data tier for the asset you are viewing is shown in the header.' },
    degraded: { dot: 'bg-amber-400', label: 'Some platform feeds delayed', text: 'text-amber-400/90', help: 'A platform-wide feed is past its freshness window. This is not the tier of the asset you are viewing — that is shown as DATA TIER in the header. Per-source detail is on the admin Operational page.' },
    reachable: { dot: 'bg-cmd-textDim', label: 'Live', text: 'text-cmd-textDim', help: 'The platform is reachable and serving. Per-source freshness telemetry is not populated on this response, so a full "all feeds fresh" status is not claimed here — the data tier for the asset you are viewing is shown in the header, and any feed-specific caveat is flagged inline.' },
    initializing: { dot: 'bg-cmd-textDim', label: 'Live feed initializing', text: 'text-cmd-textDim', help: 'Freshness telemetry is warming up.' },
    unavailable: { dot: 'bg-cmd-textDim', label: 'System status: unavailable', text: 'text-cmd-textDim', help: 'Freshness telemetry could not be read.' },
  };
  // 7.7 P1 / acceptance 29: the status line must READ the alerts source. With active (unread) alerts,
  // the footer must NOT claim "All systems operational" — active monitored breaches are a live signal
  // too. It stays client-safe (no count, no source names); the count lives on the ALERTS nav badge.
  const m = alertCount > 0
    ? { dot: 'bg-amber-400', label: 'Active alerts — see Alerts', text: 'text-amber-400/90', help: 'One or more monitored thresholds have unread alert notifications. Feed freshness is tracked separately; open Alerts for detail. This line never claims "all operational" while alerts are active.' }
    : meta[status];

  return (
    <footer className="shrink-0 border-t border-[rgba(31,41,55,0.9)] bg-cmd-bg px-4 py-1.5" role="contentinfo">
      <div className="flex items-center gap-2 text-[10px] font-mono" aria-live="polite" title={m.help}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.dot}`} aria-hidden="true" />
        <span className={m.text}>{m.label}</span>
      </div>
    </footer>
  );
}
